import { conversationUIStateAtom, useAutoFocusAfterAnimation, useTypewriter } from "@plura/client/hooks";
import { trpc } from "@plura/shared/trpc";
import { Conversation, ConversationItem, ConversationRole, FormJourneyItem, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { v4 as uuidv4 } from 'uuid';
import { z } from "zod";
import TextArea from "../../../../molecules/TextArea";
import "./open-ended-screen.css";

// Schema definitions
const ConversationFieldItem = z.object({
  id: z.string(),
  data: z.object({
    id: z.string(),
    response: z.string(),
    type: ConversationRole,
    blockId: z.string(),
  }),
});
type ConversationFieldItem = z.infer<typeof ConversationFieldItem>;

interface Props {
  id: string;
  step: FormJourneyItem;
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode?: boolean;
  active: boolean;
  evaluationIsProcessing: boolean;
  createConversation: () => Promise<Conversation>;
}

const OpenEndedScreen = ({ id, step, editorMode, previewMode, leadForm, active, evaluationIsProcessing, createConversation }: Props) => {
  // Refs & State
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [{ hideUI }, setConversationUIState] = useAtom(conversationUIStateAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const [editorFields, setEditorFields] = useState<ConversationFieldItem[]>([]);
  const prevStepContentRef = useRef(step.content);
  const generationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isGeneratingRef = useRef(false);

  // Form Context
  const { getValues } = useFormContext();
  const { fields, append } = useFieldArray({ name: step.blockId });
  const _fields = fields as ConversationFieldItem[];

  // Mutations
  const generateOpeningQuestion = trpc.leadForm.generateOpeningStepQuestion.useMutation();
  const generateEditorQuestion = trpc.leadForm.generateEditorStepQuestion.useMutation();

  // Derived State
  const messagePairs = _fields.reduce((acc, field, index, array) => {
    if (field?.data?.type === 'Assistant' && array[index + 1]?.data.type === 'User') {
      acc.push([field, array[index + 1]]);
    }
    return acc;
  }, [] as [ConversationFieldItem, ConversationFieldItem][]);

  const totalPages = messagePairs.length;
  const currentStepConversation = getValues(step.blockId) as ConversationItem[];
  const lastAssistantMessage = (editorMode || previewMode)
    ? editorFields.find(field => field.data.type === 'Assistant')?.data?.response ?? ''
    : messagePairs[currentPage]?.[0]?.data?.response ?? '';

  // Typewriter effect
  const { text: conversationText, typingDone, resetTyping } = useTypewriter(
    lastAssistantMessage,
    15,
    editorMode || !!previewMode,
    leadForm.transitionSpeed
  );

  // Editor Mode Question Generation
  const generateEditorStepQuestion = useCallback(async (content: string) => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    try {
      resetTyping();

      const response = await generateEditorQuestion.mutateAsync({
        collectionId: leadForm.collectionId,
        journeyItemId: step.blockId,
        content,
      });

      if (content === step.content) {
        setEditorFields([
          {
            id: 'assistant-message',
            data: {
              id: 'assistant-message',
              type: 'Assistant',
              response: response,
              blockId: step.blockId
            }
          },
          {
            id: 'user-message',
            data: {
              id: 'user-message',
              type: 'User',
              response: '',
              blockId: step.blockId
            }
          },
        ]);
      }
    } catch (error) {
      console.error('Editor question generation failed:', error);
    } finally {
      isGeneratingRef.current = false;
    }
  }, [generateEditorQuestion, leadForm.collectionId, step.blockId, resetTyping]);

  // Initial Setup Effect
  useEffect(() => {
    if (!active) return;

    const initializeConversation = async () => {
      let conversationId = getValues('conversationId') as string;

      if (!conversationId) {
        if (editorMode || previewMode) {
          conversationId = uuidv4();
        } else {
          const conversation = await createConversation();
          conversationId = conversation.id;
        }
      }

      if (!currentStepConversation.length && !editorMode && !previewMode) {
        await generateOpeningQuestion.mutateAsync({
          about: leadForm.about ?? '',
          conversationId: conversationId as string,
          journeyStep: step,
          overview: leadForm.overview ?? '',
          tone: leadForm.tone ?? '',
          journeyItems: leadForm.journeyItems ?? [],
          snippets: leadForm.snippets ?? [],
        }).then(res => {
          append({ data: { id: uuidv4(), type: 'Assistant', response: res.question, blockId: step.blockId } });
          append({ data: { id: uuidv4(), type: 'User', response: '', blockId: step.blockId } });
        });
      }

      setCurrentPage(messagePairs.length - 1);
    };

    initializeConversation();
  }, [active]);

  // Initial editor/preview mode setup
  useEffect(() => {
    const isEditorLike = editorMode || previewMode;
    if (!isEditorLike || !active) {
      if (isEditorLike && !active && editorFields.length) {
        setEditorFields([])
        resetTyping();
      }
      return;
    }

    if (editorMode) {
      setConversationUIState(prev => ({ ...prev, hideUI: true }));
    }

    // Generate initial question when entering editor/preview mode
    if (!editorFields.length) {
      generateEditorStepQuestion(step.content);
    }

    return () => {
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
      }
      isGeneratingRef.current = false;
    };
  }, [editorMode, previewMode, active]);

  // Handle content changes in editor/preview mode
  useEffect(() => {
    if ((!editorMode && !previewMode) || !active) return;
    if (step.content === prevStepContentRef.current) return;

    if (generationTimeoutRef.current) {
      clearTimeout(generationTimeoutRef.current);
    }

    generationTimeoutRef.current = setTimeout(() => {
      prevStepContentRef.current = step.content;
      generateEditorStepQuestion(step.content);
    }, 3000);

    return () => {
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
      }
    };
  }, [step.content, editorMode, previewMode, active]);

  // Evaluation Processing Effect
  useEffect(() => {
    if (evaluationIsProcessing) {
      resetTyping();
    }
  }, [evaluationIsProcessing]);

  // Auto-focus Effect
  const shouldFocus = typingDone && !editorMode && !previewMode;
  const { wrapperRef } = useAutoFocusAfterAnimation(() => inputRef.current, shouldFocus);

  // Message Pairs Update Effect
  useEffect(() => {
    const newMessagePairs = _fields.reduce((acc, field, index, array) => {
      if (field.data?.type === 'Assistant' && array[index + 1]?.data.type === 'User') {
        acc.push([field, array[index + 1]]);
      }
      return acc;
    }, [] as [ConversationFieldItem, ConversationFieldItem][]);

    setCurrentPage(newMessagePairs.length > 0 ? newMessagePairs.length - 1 : 0);
  }, [_fields]);

  const renderMessage = (field: ConversationFieldItem) => {
    if (field.data.type === 'Assistant') {
      return (
        <AnimatePresence mode="wait" initial={true}>
          {evaluationIsProcessing ? (
            <motion.div
              className={'defaultLoader'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          ) : (
            <motion.h4
              className={cn(
                inputRef.current?.value.length && 'semiFade',
                'question',
                'question',
                !typingDone && 'typing'
              )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: field.data.response === lastAssistantMessage
                    ? conversationText
                    : field.data.response
                }}
              />
            </motion.h4>
          )}
        </AnimatePresence>
      );
    }

    return (
      <motion.div
        ref={wrapperRef}
        className={'answerInputWrapper'}
        initial={{ opacity: 0 }}
        animate={{ opacity: typingDone && !evaluationIsProcessing ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className={'fieldWrapper'}>
          <TextArea
            ref={inputRef}
            id={`${step.blockId}.${_fields.findIndex(f => f.id === field.id)}.data.response`}
            defaultValue={field.data.response}
            withResize
            placeholder="Type here..."
            className={cn(
              'answerInput',
              'answerInputWithBorder',
              !inputRef.current?.value.length && 'empty'
            )}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
          />
        </div>
      </motion.div>
    );
  };

  return (
    <div className={cn('questionInner')}>
      <div>
        {(generateOpeningQuestion.isPending || generateEditorQuestion.isPending) ? (
          <motion.div
            className={cn('typing', 'defaultLoader')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        ) : (
          ((editorMode || previewMode) ? editorFields : messagePairs[currentPage])?.map((field) => (
            <div key={field.id} className={'message'}>
              {renderMessage(field)}
            </div>
          ))
        )}
      </div>

      {!generateOpeningQuestion.isPending && !evaluationIsProcessing && typingDone && (
        <motion.div
          className={'pagination'}
          initial={{ opacity: 0 }}
          exit={{ opacity: 0 }}
          animate={{ opacity: totalPages > 1 ? 1 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <span className="label label-green">{`${currentPage + 1}/${totalPages}`}</span>
        </motion.div>
      )}
    </div>
  );
};

export default OpenEndedScreen;